/**
 * Provider COST of the LLMs this platform calls, in USD, and the conversions
 * from token usage to dollars.
 *
 * This is the single provider-cost source (PD-16759). It answers "what did
 * this call cost Workd", which feeds `llm_cost` on agent messages, spend
 * budgets in the background pipelines and, later, `costUsd` on usage events.
 * It has nothing to do with what a tenant is charged: customer credits come
 * from the versioned `ai_rate_card` that Workd admins manage in config-web.
 *
 * Why code constants and not a table: provider prices change rarely and are
 * engineering-owned, and a git history of each rate is the audit trail. Raw
 * token counts are always stored next to any computed cost, so a cost is
 * recomputable if a rate here was wrong for a period.
 *
 * Rates are provider LIST prices in USD per MILLION tokens. Anthropic rates
 * were verified against the Anthropic model catalog (2026-08-20; Opus 4.8 on
 * 2026-10-05); OpenAI rates against OpenAI's public pricing (2026-07-03;
 * gpt-5.4 on 2026-10-05). Where an introductory discount exists we keep the
 * standard sticker rate: estimates and budgets should be conservative, and a
 * budget tuned against a temporary discount would silently loosen when it
 * ends.
 *
 * Cache columns:
 * - `cacheReadUsdPerMTok` is the discounted rate for prompt tokens served from
 *   the provider's prompt cache (Anthropic cache reads, OpenAI cached input).
 * - `cacheWriteUsdPerMTok` is Anthropic's cache-creation rate (1.25x input,
 *   5-minute TTL). OpenAI has no write premium, so its value equals input.
 */

export type LlmProviderName = 'anthropic' | 'openai';

export interface LlmModelCostModel {
	/** Model id as sent on the API request (or its alias for dated snapshots). */
	modelId: string;
	provider: LlmProviderName;
	/** USD per 1,000,000 fresh (uncached) input tokens. */
	inputUsdPerMTok: number;
	/** USD per 1,000,000 output tokens. */
	outputUsdPerMTok: number;
	/** USD per 1,000,000 cache-read input tokens. */
	cacheReadUsdPerMTok: number;
	/** USD per 1,000,000 cache-write input tokens. */
	cacheWriteUsdPerMTok: number;
}

/**
 * Every model with a known provider cost. A model the platform supports must
 * have an entry here; `llm-pricing.test.ts` fails otherwise.
 *
 * Retired OpenAI models (gpt-4.1, gpt-4o) stay listed: messages recorded
 * before the cutover carry those names, and their cost must stay
 * recomputable.
 */
export const LLM_MODEL_COSTS: Array<LlmModelCostModel> = [
	// --- Anthropic: current ---
	{
		modelId: 'claude-haiku-4-5',
		provider: 'anthropic',
		inputUsdPerMTok: 1,
		outputUsdPerMTok: 5,
		cacheReadUsdPerMTok: 0.1,
		cacheWriteUsdPerMTok: 1.25,
	},
	{
		modelId: 'claude-sonnet-5',
		provider: 'anthropic',
		// Anthropic list price, checked 2026-10-05: $2 / $10 per MTok, cache
		// read 0.1x and 5-minute cache write 1.25x of input.
		inputUsdPerMTok: 2,
		outputUsdPerMTok: 10,
		cacheReadUsdPerMTok: 0.2,
		cacheWriteUsdPerMTok: 2.5,
	},
	{
		modelId: 'claude-opus-5',
		provider: 'anthropic',
		inputUsdPerMTok: 5,
		outputUsdPerMTok: 25,
		cacheReadUsdPerMTok: 0.5,
		cacheWriteUsdPerMTok: 6.25,
	},
	{
		modelId: 'claude-opus-4-8',
		provider: 'anthropic',
		inputUsdPerMTok: 5,
		outputUsdPerMTok: 25,
		cacheReadUsdPerMTok: 0.5,
		cacheWriteUsdPerMTok: 6.25,
	},
	// --- Anthropic: older generations still selectable on agents ---
	{
		modelId: 'claude-sonnet-4-20250514',
		provider: 'anthropic',
		inputUsdPerMTok: 3,
		outputUsdPerMTok: 15,
		cacheReadUsdPerMTok: 0.3,
		cacheWriteUsdPerMTok: 3.75,
	},
	{
		modelId: 'claude-opus-4-20250514',
		provider: 'anthropic',
		inputUsdPerMTok: 15,
		outputUsdPerMTok: 75,
		cacheReadUsdPerMTok: 1.5,
		cacheWriteUsdPerMTok: 18.75,
	},
	{
		modelId: 'claude-3-5-sonnet-20241022',
		provider: 'anthropic',
		inputUsdPerMTok: 3,
		outputUsdPerMTok: 15,
		cacheReadUsdPerMTok: 0.3,
		cacheWriteUsdPerMTok: 3.75,
	},
	{
		modelId: 'claude-3-5-haiku-20241022',
		provider: 'anthropic',
		inputUsdPerMTok: 0.8,
		outputUsdPerMTok: 4,
		cacheReadUsdPerMTok: 0.08,
		cacheWriteUsdPerMTok: 1,
	},
	{
		modelId: 'claude-3-opus-20240229',
		provider: 'anthropic',
		inputUsdPerMTok: 15,
		outputUsdPerMTok: 75,
		cacheReadUsdPerMTok: 1.5,
		cacheWriteUsdPerMTok: 18.75,
	},
	{
		modelId: 'claude-3-sonnet-20240229',
		provider: 'anthropic',
		inputUsdPerMTok: 3,
		outputUsdPerMTok: 15,
		cacheReadUsdPerMTok: 0.3,
		cacheWriteUsdPerMTok: 3.75,
	},
	{
		modelId: 'claude-3-haiku-20240307',
		provider: 'anthropic',
		inputUsdPerMTok: 0.25,
		outputUsdPerMTok: 1.25,
		cacheReadUsdPerMTok: 0.03,
		cacheWriteUsdPerMTok: 0.3,
	},
	// --- OpenAI: current ---
	{
		modelId: 'gpt-5.4',
		provider: 'openai',
		inputUsdPerMTok: 2.5,
		outputUsdPerMTok: 15,
		cacheReadUsdPerMTok: 0.25,
		cacheWriteUsdPerMTok: 2.5,
	},
	{
		modelId: 'gpt-5.4-mini',
		provider: 'openai',
		inputUsdPerMTok: 0.75,
		outputUsdPerMTok: 4.5,
		cacheReadUsdPerMTok: 0.075,
		cacheWriteUsdPerMTok: 0.75,
	},
	{
		modelId: 'gpt-5.4-nano',
		provider: 'openai',
		inputUsdPerMTok: 0.2,
		outputUsdPerMTok: 1.25,
		cacheReadUsdPerMTok: 0.02,
		cacheWriteUsdPerMTok: 0.2,
	},
	// --- OpenAI: retired (kept so historical cost stays recomputable) ---
	{
		modelId: 'gpt-4.1',
		provider: 'openai',
		inputUsdPerMTok: 2,
		outputUsdPerMTok: 8,
		cacheReadUsdPerMTok: 0.5,
		cacheWriteUsdPerMTok: 2,
	},
	{
		modelId: 'gpt-4.1-mini',
		provider: 'openai',
		inputUsdPerMTok: 0.4,
		outputUsdPerMTok: 1.6,
		cacheReadUsdPerMTok: 0.1,
		cacheWriteUsdPerMTok: 0.4,
	},
	{
		modelId: 'gpt-4.1-nano',
		provider: 'openai',
		inputUsdPerMTok: 0.1,
		outputUsdPerMTok: 0.4,
		cacheReadUsdPerMTok: 0.025,
		cacheWriteUsdPerMTok: 0.1,
	},
	{
		modelId: 'gpt-4o',
		provider: 'openai',
		inputUsdPerMTok: 2.5,
		outputUsdPerMTok: 10,
		cacheReadUsdPerMTok: 1.25,
		cacheWriteUsdPerMTok: 2.5,
	},
	{
		modelId: 'gpt-4o-mini',
		provider: 'openai',
		inputUsdPerMTok: 0.15,
		outputUsdPerMTok: 0.6,
		cacheReadUsdPerMTok: 0.075,
		cacheWriteUsdPerMTok: 0.15,
	},
];

/**
 * A provider snapshot suffix: `-20251001` (Anthropic) or `-2025-04-14`
 * (OpenAI).
 */
const SNAPSHOT_SUFFIX = /^-(\d{8}|\d{4}-\d{2}-\d{2})$/;

/**
 * Resolve a model name to one of `keys`: an exact match, or a key the name
 * extends with only a dated snapshot suffix. A dated snapshot
 * (`claude-haiku-4-5-20251001`, `gpt-4.1-mini-2025-04-14`) bills at its
 * alias's rate, but a different model that merely shares a prefix
 * (`gpt-5.4-mini` against `gpt-5.4`, `claude-sonnet-50`) never resolves, so
 * it is reported as unpriced rather than charged at another model's rate.
 *
 * Shared by the cost table here and the credit rate card, so a model name
 * resolves to the same entry in both.
 */
export const resolveModelKey = (
	modelId: string | null | undefined,
	keys: Iterable<string>,
): string | null => {
	if (modelId == null || modelId === '') {
		return null;
	}

	let snapshotOf: string | null = null;

	for (const key of keys) {
		if (key === modelId) {
			return key;
		}

		if (
			modelId.startsWith(key) &&
			SNAPSHOT_SUFFIX.test(modelId.slice(key.length))
		) {
			snapshotOf = key;
		}
	}

	return snapshotOf;
};

/** The provider cost entry for a model, or null when it has none. */
export const findLlmModelCost = (
	modelId: string | null | undefined,
): LlmModelCostModel | null => {
	const key = resolveModelKey(
		modelId,
		LLM_MODEL_COSTS.map((entry) => entry.modelId),
	);

	if (key == null) {
		return null;
	}

	return LLM_MODEL_COSTS.find((entry) => entry.modelId === key) ?? null;
};

/**
 * The hosted web_search surcharge: $10 per 1,000 searches, billed per
 * request regardless of model.
 */
export const WEB_SEARCH_USD_PER_REQUEST = 0.01;

/**
 * A model the background pipelines (email ingest, external lookup, product
 * matching) may be configured to run, with its picker label.
 */
export interface LlmModelPricingModel extends LlmModelCostModel {
	/** Human label for step-config model pickers. */
	label: string;
}

const pipelineModel = (modelId: string, label: string): LlmModelPricingModel => {
	const cost = LLM_MODEL_COSTS.find((entry) => entry.modelId === modelId);

	// Fails at module load, so a typo here breaks every test run immediately.
	if (cost == null) {
		throw new Error(`No provider cost entry for pipeline model ${modelId}`);
	}

	return { ...cost, label };
};

/**
 * Models the background pipelines may run. The rates come from
 * `LLM_MODEL_COSTS`, so there is one number per model. Order matters: step
 * config pickers render this order, so keep it cheapest-first.
 */
export const LLM_MODEL_PRICING: Array<LlmModelPricingModel> = [
	pipelineModel('claude-haiku-4-5', 'Claude Haiku 4.5 (fastest, lowest cost)'),
	pipelineModel('claude-sonnet-5', 'Claude Sonnet 5 (balanced)'),
	pipelineModel('claude-opus-5', 'Claude Opus 5 (highest capability)'),
];

/**
 * The pipeline-picker entry for a model, or null when the model is not one a
 * pipeline may be configured to run. Use `findLlmModelCost` to price any
 * model; use this to validate a pipeline's configured model.
 */
export const findLlmModelPricing = (
	modelId: string | null | undefined,
): LlmModelPricingModel | null => {
	const key = resolveModelKey(
		modelId,
		LLM_MODEL_PRICING.map((entry) => entry.modelId),
	);

	if (key == null) {
		return null;
	}

	return LLM_MODEL_PRICING.find((entry) => entry.modelId === key) ?? null;
};

/**
 * Anthropic-style usage: `inputTokens` excludes cache reads and cache writes,
 * which are reported separately.
 */
export interface LlmUsageForPricingModel {
	inputTokens: number;
	outputTokens: number;
	cacheReadInputTokens?: number | null;
	cacheCreationInputTokens?: number | null;
	webSearchRequests?: number | null;
}

/**
 * Convert one ledger row's worth of usage to dollars, or null when the model
 * has no cost entry. Callers must surface the null (an "unpriceable"
 * warning), never treat it as zero: zero-cost is exactly the wrong failure
 * mode for a budgeting system.
 */
export const usageToUsd = (
	usage: LlmUsageForPricingModel,
	modelId: string | null | undefined,
): number | null => {
	const cost = findLlmModelCost(modelId);

	if (cost == null) {
		return null;
	}

	const perTok = (tokens: number | null | undefined, usdPerMTok: number) =>
		((tokens ?? 0) * usdPerMTok) / 1_000_000;

	return (
		perTok(usage.inputTokens, cost.inputUsdPerMTok) +
		perTok(usage.outputTokens, cost.outputUsdPerMTok) +
		perTok(usage.cacheReadInputTokens, cost.cacheReadUsdPerMTok) +
		perTok(usage.cacheCreationInputTokens, cost.cacheWriteUsdPerMTok) +
		(usage.webSearchRequests ?? 0) * WEB_SEARCH_USD_PER_REQUEST
	);
};

/**
 * OpenAI-style usage, as the agent providers report it: `promptTokens`
 * INCLUDES `cachedTokens`.
 */
export interface LlmUsageForCostModel {
	promptTokens: number;
	completionTokens: number;
	cachedTokens?: number;
}

/**
 * USD cost of one agent completion, or null (never a guess) when the model
 * has no cost entry. Cached tokens are billed at the cache-read rate and
 * subtracted from the fresh-input portion; a `cachedTokens` value larger than
 * `promptTokens` is clamped (defensive: providers should never report that).
 *
 * Rounded to 6 decimal places to match the `llm_cost` DECIMAL(12,6) column.
 */
export const computeLlmCost = (
	modelId: string | null | undefined,
	usage: LlmUsageForCostModel | null | undefined,
): number | null => {
	if (usage == null) {
		return null;
	}

	const cost = findLlmModelCost(modelId);

	if (cost == null) {
		return null;
	}

	const cachedTokens = Math.min(usage.cachedTokens ?? 0, usage.promptTokens);
	const freshTokens = usage.promptTokens - cachedTokens;
	const usd =
		(freshTokens * cost.inputUsdPerMTok +
			cachedTokens * cost.cacheReadUsdPerMTok +
			usage.completionTokens * cost.outputUsdPerMTok) /
		1_000_000;

	return Math.round(usd * 1_000_000) / 1_000_000;
};

/** Render a dollar amount for UI/step output: cents precision, floor at 1c shown as "<$0.01" when non-zero. */
export const formatUsdEstimate = (usd: number): string => {
	if (usd > 0 && usd < 0.01) {
		return '<$0.01';
	}

	return `$${usd.toFixed(2)}`;
};
